const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

export default {
  data() {
    return {
      activeTouches: [],
      prevDiff: -1,
      zoomScale: 1,
      translateX: 0,
      translateY: 0,
      lastPanX: 0,
      lastPanY: 0
    };
  },
  computed: {
    pinchZoomTouchAction() {
      if (this.zoomScale > 1 || this.activeTouches.length >= 2) {
        return 'none'; // 줌 상태 또는 멀티 터치인 경우 모든 터치 액션 비활성화
      }
      return 'auto'; // 기본 상태에서는 모든 터치 액션 활성화
    },
    pinchZoomTransform() {
      if (
        this.zoomScale === 1 &&
        this.translateX === 0 &&
        this.translateY === 0
      ) {
        return 'none';
      }

      return `translate3d(${this.translateX}px, ${this.translateY}px, 0px) scale(${this.zoomScale})`;
    }
  },
  mounted() {
    this.setupPinchZoom();
  },
  beforeDestroy() {
    this.removePinchZoom();
  },
  methods: {
    setupPinchZoom() {
      const containerEl = this.$refs.pinchZoomContainer;
      if (containerEl) {
        containerEl.addEventListener('pointerdown', this.pointerdownHandler);
        containerEl.addEventListener('pointermove', this.pointermoveHandler);
        containerEl.addEventListener('pointerup', this.pointerupHandler);
        containerEl.addEventListener('pointercancel', this.pointerupHandler);
        containerEl.addEventListener('pointerout', this.pointerupHandler);
        containerEl.addEventListener('pointerleave', this.pointerupHandler);
      }
    },
    removePinchZoom() {
      const containerEl = this.$refs.pinchZoomContainer;
      if (containerEl) {
        containerEl.removeEventListener('pointerdown', this.pointerdownHandler);
        containerEl.removeEventListener('pointermove', this.pointermoveHandler);
        containerEl.removeEventListener('pointerup', this.pointerupHandler);
        containerEl.removeEventListener('pointercancel', this.pointerupHandler);
        containerEl.removeEventListener('pointerout', this.pointerupHandler);
        containerEl.removeEventListener('pointerleave', this.pointerupHandler);
      }
    },
    pointerdownHandler(ev) {
      this.activeTouches.push({
        pointerId: ev.pointerId,
        clientX: ev.clientX,
        clientY: ev.clientY
      });

      // 단일 터치일 때 드래그 시작점 저장
      if (this.activeTouches.length === 1) {
        this.lastPanX = ev.clientX;
        this.lastPanY = ev.clientY;
      }
    },
    pointermoveHandler(ev) {
      // 줌 상태이거나 멀티터치일 때만 동작하도록 함
      if (this.zoomScale <= 1 && this.activeTouches.length < 2) {
        return;
      }

      ev.preventDefault();

      // 활성 터치 목록에서 현재 이벤트 정보 업데이트
      for (let i = 0; i < this.activeTouches.length; i++) {
        if (ev.pointerId === this.activeTouches[i].pointerId) {
          this.activeTouches[i] = ev;
          break;
        }
      }

      if (this.activeTouches.length === 2) {
        this.handlePinchZoom();
      } else if (this.activeTouches.length === 1 && this.zoomScale > 1) {
        this.handlePanGesture(ev);
      }
    },
    handlePanGesture(ev) {
      const deltaX = ev.clientX - this.lastPanX;
      const deltaY = ev.clientY - this.lastPanY;

      this.translateX += deltaX;
      this.translateY += deltaY;

      this.lastPanX = ev.clientX;
      this.lastPanY = ev.clientY;

      this.constrainTranslation();
    },
    handlePinchZoom() {
      const [touch1, touch2] = this.activeTouches;

      // 두 포인터 사이의 거리 계산
      const curDiff = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      // 핀치 중심점 계산
      const centerX = (touch1.clientX + touch2.clientX) / 2;
      const centerY = (touch1.clientY + touch2.clientY) / 2;

      if (this.prevDiff > 0) {
        const scaleFactor = curDiff / this.prevDiff;
        const newScale = Math.max(
          MIN_ZOOM,
          Math.min(MAX_ZOOM, this.zoomScale * scaleFactor)
        );

        // 줌 중심점을 기준으로 translate 조정
        if (this.zoomScale !== newScale) {
          const containerEl = this.$refs.pinchZoomContainer;
          if (containerEl) {
            const rect = containerEl.getBoundingClientRect();
            const relativeX = centerX - rect.left - rect.width / 2;
            const relativeY = centerY - rect.top - rect.height / 2;

            const scaleRatio = newScale / this.zoomScale;

            this.translateX += relativeX * (1 - scaleRatio);
            this.translateY += relativeY * (1 - scaleRatio);
          }
        }

        this.zoomScale = newScale;
      }

      this.prevDiff = curDiff;
      this.constrainTranslation();
    },
    constrainTranslation() {
      const containerEl = this.$refs.pinchZoomContainer;
      if (!containerEl) return;

      // 이동 범위 제한
      const rect = containerEl.getBoundingClientRect();
      const scaledWidth = rect.width * this.zoomScale;
      const scaledHeight = rect.height * this.zoomScale;

      const maxTranslateX = Math.max(0, (scaledWidth - rect.width) / 2);
      const maxTranslateY = Math.max(0, (scaledHeight - rect.height) / 2);

      this.translateX = Math.max(
        -maxTranslateX,
        Math.min(maxTranslateX, this.translateX)
      );
      this.translateY = Math.max(
        -maxTranslateY,
        Math.min(maxTranslateY, this.translateY)
      );
    },
    pointerupHandler(ev) {
      this.removeActiveTouch(ev);

      if (this.activeTouches.length < 2) {
        this.prevDiff = -1;
      }

      if (this.zoomScale <= 1) {
        this.resetTransform();
      }
    },
    removeActiveTouch(ev) {
      for (let i = 0; i < this.activeTouches.length; i++) {
        if (this.activeTouches[i].pointerId === ev.pointerId) {
          this.activeTouches.splice(i, 1);
          break;
        }
      }
    },
    resetTransform() {
      this.zoomScale = 1;
      this.translateX = 0;
      this.translateY = 0;
    }
  }
};
